import { desktopUpdateTurnsInWords, type DesktopUpdateNotice } from "./desktopUpdate.logic";
import type { ThreadToastData } from "./ui/toast";

export interface DesktopUpdateNoticeActions {
  /** One click: download if needed, wait for running work, install and reopen. */
  readonly update: () => void;
  /** Settings at the Update automatically switch; absent on Desks without it. */
  readonly openSettings?: (() => void) | undefined;
}

const WAITS_FOR_WORK = "Cleo waits for running conversations to finish, then reopens.";

/**
 * The toast one update notice becomes. The same toast is updated in place as
 * the notice moves on, so every phase names every field: a phase without a
 * control says `actionProps: undefined` and an empty `data` to take the
 * previous ones away.
 *
 * Can 30: one Update does the whole job and waits for running work instead of
 * interrupting it, so the found, replaced and downloaded phases offer Update
 * beside Settings (Update automatically) rather than Download, then Restart.
 *
 * It links nowhere: the Neucleos release origin serves no release-notes page,
 * and nothing in the product points at GitHub
 * (`GOALS/GOALS-RELEASE-DISTRIBUTION.html`).
 */
export function desktopUpdateNoticeToast(
  notice: DesktopUpdateNotice,
  actions: DesktopUpdateNoticeActions,
) {
  const update = { children: "Update", onClick: actions.update };
  // `data` replaces the previous phase's whole, so `quiet` takes Settings away.
  const quiet: ThreadToastData = {};
  const offer: ThreadToastData = actions.openSettings
    ? { secondaryActionProps: { children: "Settings", onClick: actions.openSettings } }
    : quiet;
  switch (notice.kind) {
    case "available":
      return {
        type: "info",
        title: `Cleo ${notice.version} is available`,
        description: `Update installs it. ${WAITS_FOR_WORK}`,
        timeout: 0,
        actionProps: update,
        data: offer,
      };
    case "replaced":
      return {
        type: "info",
        title: `Cleo ${notice.version} is available`,
        description: `It replaces the Cleo ${notice.replacedVersion} you downloaded. ${WAITS_FOR_WORK}`,
        timeout: 0,
        actionProps: update,
        data: offer,
      };
    case "downloading":
      return {
        type: "loading",
        title: `Downloading Cleo ${notice.version}`,
        description:
          notice.percent === null
            ? "Starting the download…"
            : `${Math.floor(notice.percent)}% downloaded`,
        timeout: 0,
        actionProps: undefined,
        data: quiet,
      };
    case "downloaded":
      return {
        type: "success",
        title: `Cleo ${notice.version} is ready to install`,
        description: WAITS_FOR_WORK,
        timeout: 0,
        actionProps: update,
        data: offer,
      };
    case "waiting":
      return {
        type: "loading",
        title: "Updating after Cleo finishes",
        description: `Cleo ${notice.version} installs when ${desktopUpdateTurnsInWords(notice.turns)}.`,
        timeout: 0,
        actionProps: undefined,
        data: quiet,
      };
    case "installing":
      return {
        type: "loading",
        title: `Updating to Cleo ${notice.version}`,
        description:
          notice.message ?? "It installs once no conversation is working, then Cleo Code reopens.",
        timeout: 0,
        actionProps: undefined,
        data: quiet,
      };
    case "download-failed":
      return {
        type: "error",
        title: `Could not download Cleo ${notice.version}`,
        description: notice.message ?? "The download did not finish.",
        timeout: 0,
        actionProps: { children: "Try again", onClick: actions.update },
        data: quiet,
      };
    case "install-failed":
      return {
        type: "error",
        title: `Could not install Cleo ${notice.version}`,
        description: notice.message ?? "The install did not start.",
        timeout: 0,
        actionProps: { children: "Try again", onClick: actions.update },
        data: quiet,
      };
  }
}
